import React, { useEffect, useState } from 'react';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import DateTime from '../../Automations/AutomationModal/ScheduleArea/DateTime';
import ResultsTable from '../../Automations/BacktestModal/ResultsTable';
import { doBacktest } from '../../../services/AutomationsService';
import { getStrategy, getRegimeSweep } from '../../../services/StrategiesService';
import { processError } from '../../../services/ServiceUtils';
import { getValueOrDefault, initializeModal } from '../../../utils/ModalUtils';
import { getToday, getYesterday } from '../../../utils/DateUtils';
import { isWildcard, getQuoteFromWildcard } from '../../../utils/SymbolUtils';
import { formatNumber } from '../../../utils/NumberUtils';

const REGIME_PT = {
    TRENDING_UP: 'Alta', TRENDING_DOWN: 'Queda', RANGING: 'Lateral',
    HIGH_VOLATILITY: 'Whipsaw', RECUPERACAO: 'Recuperação (V)', RUPTURA: 'Ruptura', OUTRO: 'Outro'
};

const DEFAULT_PAST = { startBase: 0, startQuote: 1000, startTime: new Date(getYesterday()), endTime: new Date(getToday()) };

/**
 * Modal "Testar estratégia" (item 3 da fila): unifica backtest histórico (aba Passado, candles
 * reais) e regime sweep sintético (aba Sintético, biblioteca de regimes — bull/bear/range/
 * whipsaw/ruptura) num único ponto de entrada. Sintético não tem parâmetro nenhum: roda a
 * biblioteca inteira, read-only (não persiste regimeProfile — isso é o botão 🎯 separado).
 * props: { data: { id, symbol, buyAutomation, sellAutomation } }
 */
function TestStrategyModal(props) {
    const strategy = props.data || {};
    const id = strategy.id;
    const wildcard = isWildcard(strategy.symbol);
    const concreteSymbol = wildcard ? `BTC${getQuoteFromWildcard(strategy.symbol)}` : strategy.symbol;

    const [tab, setTab] = useState('past');

    // ── Passado (backtest histórico) ────────────────────────────────────────────────────────
    const [past, setPast] = useState(DEFAULT_PAST);
    const [pastResult, setPastResult] = useState(null);
    const [pastTesting, setPastTesting] = useState(false);
    const [pastError, setPastError] = useState('');

    function onPastInputChange(event) {
        setPast(prev => ({ ...prev, [event.target.id]: event.target.value }));
    }

    // Busca a estratégia completa (inclui supportingAutomations) em vez de usar só buyAutomation/
    // sellAutomation da linha da lista — estratégias multi-automação (AI Full Auto) têm
    // buyAutomationId/sellAutomationId NULOS e linkam tudo por strategyId (mesmo padrão do
    // simulateStrategy no backend); sem isso o backtest via automationIds vazio.
    async function onPastSubmit() {
        if (pastTesting || !id) return;
        setPastError('');
        setPastTesting(true);
        try {
            const full = await getStrategy(id);
            const automationIds = [...new Set([
                full.buyAutomationId,
                full.sellAutomationId,
                ...(full.supportingAutomations || []).map(a => a.id)
            ].filter(Boolean))];
            if (!automationIds.length) {
                setPastError('Estratégia sem automação de compra/venda para testar.');
                setPastTesting(false);
                return;
            }

            const payload = {
                automationIds,
                startBase: parseFloat(past.startBase) || 0,
                startQuote: parseFloat(past.startQuote) || 0,
                startTime: past.startTime,
                endTime: past.endTime
            };
            if (wildcard) payload.concreteSymbol = concreteSymbol;

            const result = await doBacktest(payload);
            result.startQtyUsd = parseFloat(result.startQtyUsd);
            result.endQtyUsd = parseFloat(result.endQtyUsd);
            result.profitUsd = parseFloat(result.profitUsd);
            result.profitPerc = parseFloat(result.profitPerc);
            result.winRate = parseFloat(result.winRate) || 0;
            result.maxDrawdown = parseFloat(result.maxDrawdown) || 0;
            result.sharpe = parseFloat(result.sharpe) || 0;
            result.equityCurve = result.equityCurve || [];
            setPastResult(result);
            setPastTesting(false);
        } catch (err) {
            setPastError(processError(err));
            setPastTesting(false);
        }
    }

    // ── Sintético (regime sweep) ────────────────────────────────────────────────────────────
    const [sweep, setSweep] = useState(null);
    const [sweepLoading, setSweepLoading] = useState(false);
    const [sweepError, setSweepError] = useState('');

    // Só busca se ainda não tem resultado — sweep!=null já basta como guarda (evita repetir ao só
    // trocar de aba); em erro, sweep continua null e a próxima visita à aba tenta de novo.
    function loadSweep() {
        if (!id || sweep) return;
        setSweepLoading(true);
        setSweepError('');
        getRegimeSweep(id, wildcard ? concreteSymbol : null)
            .then(setSweep)
            .catch(err => setSweepError(processError(err)))
            .finally(() => setSweepLoading(false));
    }

    function onTabClick(next) {
        setTab(next);
    }

    function initializeStates() {
        setTab('past');
        setPast(DEFAULT_PAST);
        setPastResult(null);
        setPastTesting(false);
        setPastError('');
        setSweep(null);
        setSweepLoading(false);
        setSweepError('');
    }

    useEffect(() => {
        initializeModal('modalTestStrategy', () => initializeStates());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function formIsFilled() {
        return past.startTime && past.endTime && past.startQuote !== '' && past.startBase !== '';
    }

    function regimeRows() {
        if (!sweep || !sweep.byRegime) return [];
        return Object.keys(sweep.byRegime).map(k => ({ key: k, ...sweep.byRegime[k] }));
    }

    function vsHoldBadge(value) {
        if (value === undefined || value === null) return null;
        const cls = value > 0 ? 'bg-success' : value < 0 ? 'bg-danger' : 'bg-secondary';
        return <span className={`badge ${cls}`}>{value > 0 ? '+' : ''}{formatNumber(value, 2)}%</span>;
    }

    return (
        <div className="modal fade" id="modalTestStrategy" tabIndex="-1" role="dialog" aria-labelledby="modalTestStrategyTitle" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTestStrategyTitle">Testar estratégia{strategy.name ? `: ${strategy.name}` : ''}</p>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>

                    <div className="modal-body">
                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'past' ? 'active' : ''}`} onClick={() => onTabClick('past')}>
                                    Passado
                                </button>
                            </li>
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'synthetic' ? 'active' : ''}`} onClick={() => onTabClick('synthetic')}>
                                    Simulador
                                </button>
                            </li>
                        </ul>

                        {
                            tab === 'past'
                                ? (
                                    <React.Fragment>
                                        <p className="text-muted small">
                                            Roda as automações desta estratégia sobre candles reais do período escolhido.
                                        </p>
                                        {
                                            !pastResult
                                                ? (
                                                    <div className="row">
                                                        <div className="col-md-6 mb-3">
                                                            <label htmlFor="startBase">Base Inicial:</label>
                                                            <input type="number" id="startBase" className="form-control" placeholder="0" value={getValueOrDefault(past.startBase)} onChange={onPastInputChange} />
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <label htmlFor="startQuote">Quote Inicial:</label>
                                                            <input type="number" id="startQuote" className="form-control" placeholder="0" value={getValueOrDefault(past.startQuote)} onChange={onPastInputChange} />
                                                        </div>
                                                        <div className="col-6 mb-3">
                                                            <label htmlFor="startTime">Data Inicial:</label>
                                                            <DateTime id="startTime" onChange={onPastInputChange} date={getValueOrDefault(past.startTime)} />
                                                        </div>
                                                        <div className="col-6 mb-3">
                                                            <label htmlFor="endTime">Data Final:</label>
                                                            <DateTime id="endTime" onChange={onPastInputChange} date={getValueOrDefault(past.endTime)} />
                                                        </div>
                                                    </div>
                                                )
                                                : (
                                                    <React.Fragment>
                                                        <ResultsTable data={pastResult} />
                                                        <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setPastResult(null)}>
                                                            Testar de novo
                                                        </button>
                                                    </React.Fragment>
                                                )
                                        }
                                        <ActionMessage error={pastError} extraClassName="mt-2" />
                                        {
                                            !pastResult &&
                                            <button type="button" className="btn btn-sm btn-primary mt-2" onClick={onPastSubmit} disabled={pastTesting || !formIsFilled()}>
                                                {pastTesting ? 'Testando...' : 'Rodar backtest'}
                                            </button>
                                        }
                                    </React.Fragment>
                                )
                                : (
                                    <React.Fragment>
                                        <p className="text-muted small">
                                            Roda as automações direcionais desta estratégia sobre a biblioteca de regimes
                                            simulados (alta/queda/lateral/whipsaw/ruptura) — mostra o desempenho por regime e
                                            totalizado vs. buy&amp;hold. Não configura nada, não grava nada.
                                        </p>
                                        {sweepLoading && <div className="text-center py-4"><div className="spinner-border" role="status" /></div>}
                                        <ActionMessage error={sweepError} extraClassName="mt-2" />
                                        {
                                            sweep && (
                                                <React.Fragment>
                                                    <div className="mb-2 small text-muted">
                                                        Símbolo testado: <strong>{sweep.symbol}</strong> · {sweep.total.scenarios} cenários · {sweep.total.totalOperations} operações
                                                    </div>
                                                    <table className="table table-sm align-middle">
                                                        <thead>
                                                            <tr>
                                                                <th>Regime</th>
                                                                <th>Cenários</th>
                                                                <th>P&amp;L médio</th>
                                                                <th>vs. buy&amp;hold</th>
                                                                <th>Operações</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {regimeRows().map(r => (
                                                                <tr key={r.key}>
                                                                    <td>{REGIME_PT[r.key] || r.key}</td>
                                                                    <td>{r.scenarios}</td>
                                                                    <td>{formatNumber(r.avgProfitPerc, 2)}%</td>
                                                                    <td>{vsHoldBadge(r.avgVsHold)}</td>
                                                                    <td>{r.operations}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr className="fw-bold">
                                                                <td>Total</td>
                                                                <td>{sweep.total.scenarios}</td>
                                                                <td>{formatNumber(sweep.total.avgProfitPerc, 2)}%</td>
                                                                <td>{vsHoldBadge(sweep.total.avgVsHold)}</td>
                                                                <td>{sweep.total.totalOperations}</td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                    <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setSweep(null)}>
                                                        Rodar de novo
                                                    </button>
                                                </React.Fragment>
                                            )
                                        }
                                        {
                                            !sweep && !sweepLoading &&
                                            <button type="button" className="btn btn-sm btn-primary mt-2" onClick={loadSweep}>
                                                Rodar Simulação
                                            </button>
                                        }
                                    </React.Fragment>
                                )
                        }
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TestStrategyModal;
